import type { EntryPoint, EntryPointComponent, PreloadFetchPolicy } from 'react-relay';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import traceUFOPress from '@atlaskit/react-ufo/trace-press';
import type { AnalyticsAttributes } from '@atlassian/projects-goals-common/analytics';
import { createPlatformSiteAri } from '@atlassian/projects-goals-common/create-platform-site-ari';
import { getSiteId } from '@atlassian/projects-goals-common/get-site-id';
import { JSResourceForInteraction } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';

import parameters, { type ProjectPanelQuery } from './__generated__/ProjectPanelQuery.graphql';
import type { ProductName, TabType } from './types';

type EntryPointParams = { ari: string };
const fetchPolicy: PreloadFetchPolicy = 'network-only';

const atlasProjectEntryPoint: EntryPoint<EntryPointComponent<{
    queryReference: ProjectPanelQuery;
}, Record<string, never>, {
    parentProduct: ProductName;
    cloudId: string;
    analyticsAttributes?: AnalyticsAttributes;
    organizationId?: string;
    url?: string;
    defaultTab?: TabType;
}, Record<string, never>>, EntryPointParams> = createEntryPoint({
	root: JSResourceForInteraction(
		() =>
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/entry-point-fixtures/ProjectPanel" */ './ProjectPanel'
			).then((mod) => mod.ProjectPanel),
		{
			moduleId: 'atlas-project-entrypoint',
		},
	),
	getPreloadProps: ({ ari }: EntryPointParams) => {
		traceUFOPress('townsquare-project-panel-opened');

		// check for M0 or greater
		const isProjectNativeIntegrationEnabled =
			FeatureGates.getExperimentValue(
				'atlassian_projects_-_native_integration',
				'releaseVersion',
				-1,
			) >= 0;

		const siteId = getSiteId('project', ari);
		const queryContext =
			siteId && fg('townsquare_ugs_query_context') ? createPlatformSiteAri(siteId) : null;

		return {
			queries: {
				queryReference: {
					parameters,
					variables: {
						ari,
						isProjectNativeIntegrationEnabled,
						queryContext,
					},
					options: {
						fetchPolicy,
					},
				},
			},
		};
	},
});

export default atlasProjectEntryPoint;
